import { Table, TableContainer, Tbody, Td, Th, Thead, Tr } from '@chakra-ui/react'
import { useEffect, useState } from 'react'

import { ActionButton, ActionFab } from '../../../../components/ActionButton'
import { ConfirmDialog } from '../../../../components/ConfirmDialog'
import { FilterBar, FilterSearch } from '../../../../components/FilterBar'
import { TableSkeletonRows } from '../../../../components/Loading'
import { TableEmptyRow, TableErrorRow } from '../../../../components/TableEmptyRow'
import { formatDateTime, useHasRole, useAppToast } from '../../../../helpers'
import { ICourseInterface } from '../../../../interfaces/Website/Courses/CoursesCategory/ICoursesCategory'
import { CoursesCategoryService } from '../../../../services/CoursesCategory/CoursesCategoryService'
import { CoursesCategoryModal } from './CoursesCategoryModal'

const CoursesCategoryPage = () => {
  const service = new CoursesCategoryService()
  const toast = useAppToast()
  const hasRole = useHasRole()
  const permissions = {
    addCourseFilter: hasRole('add_course_filter'),
    deleteCourseFilter: hasRole('delete_course_filter')
  }

  const [categories, setCategories] = useState<ICourseInterface[]>([])
  const [filtered, setFiltered] = useState<ICourseInterface[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [search, setSearch] = useState('')

  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [idDeleting, setIdDeleting] = useState<number | null>(null)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingCategory, setEditingCategory] = useState<ICourseInterface | null>(null)

  const fetchCategories = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const data = await service.list()
      setCategories(data)
      setFiltered(data)
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar as categorias', error)
    } finally {
      setLoading(false)
    }
  }

  const closeDeleteDialog = () => {
    setIsDeleteOpen(false)
    setIdDeleting(null)
  }

  const handleDelete = async () => {
    if (!idDeleting) return

    try {
      await service.delete(idDeleting)
      toast.success('Categoria excluída com sucesso')
      fetchCategories()
    } catch (error) {
      toast.error('Erro ao excluir categoria', error)
    } finally {
      setIsDeleteOpen(false)
      setIdDeleting(null)
    }
  }

  const openCreateModal = () => {
    setEditingCategory(null)
    setIsModalOpen(true)
  }

  const openEditModal = (category: ICourseInterface) => {
    setEditingCategory(category)
    setIsModalOpen(true)
  }

  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    const term = search.toLowerCase()
    const filteredData = categories.filter((item) => item.name.toLowerCase().includes(term))
    setFiltered(filteredData)
  }, [search, categories])

  return (
    <>
      <FilterBar onClear={() => setSearch('')} isDirty={search !== ''}>
        <FilterSearch
          value={search}
          onChange={setSearch}
          placeholder="Buscar por categoria"
          // Filtragem local: nada de esperar meio segundo para filtrar uma lista já carregada.
          delay={0}
        />
      </FilterBar>
      <TableContainer>
        <Table colorScheme="gray">
          <Thead>
            <Tr>
              <Th>ID</Th>
              <Th>Nome</Th>
              <Th>Criado em</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={4} />
            ) : loadError ? (
              <TableErrorRow colSpan={4} entity="categorias" feminine onRetry={fetchCategories} />
            ) : filtered.length === 0 ? (
              <TableEmptyRow colSpan={4} entity="categoria" feminine />
            ) : (
              filtered.map((category) => (
                <Tr key={category.id}>
                  <Td>{category.id}</Td>
                  <Td>{category.name}</Td>
                  <Td>{formatDateTime(category.created_at)}</Td>
                  <Td>
                    {permissions.deleteCourseFilter && (
                      <ActionButton action="edit" onClick={() => openEditModal(category)} />
                    )}
                    {permissions.deleteCourseFilter && (
                      <ActionButton
                        action="delete"
                        onClick={() => {
                          setIsDeleteOpen(true)
                          setIdDeleting(category.id)
                        }}
                      />
                    )}
                  </Td>
                </Tr>
              ))
            )}
          </Tbody>
        </Table>
      </TableContainer>
      {permissions.addCourseFilter && <ActionFab label="Adicionar categoria de curso" onClick={openCreateModal} />}

      <CoursesCategoryModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onFinished={fetchCategories}
        initialData={editingCategory}
        service={service}
      />

      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={closeDeleteDialog}
        onConfirm={handleDelete}
        action="delete"
        entity="categoria"
        feminine
      />
    </>
  )
}

export default CoursesCategoryPage
